/**
 * <AuthTemplate> — la coquille des écrans d'avant connexion (template).
 *
 * Tous les écrans du parcours de connexion ont la même forme : le logo en tête, une flèche de
 * retour quand il y a d'où revenir, et UNE colonne étroite — pleine largeur sur un téléphone,
 * posée dans une carte à partir de `md`, parce que des champs étirés sur un écran d'ordinateur ne
 * se lisent plus comme un formulaire.
 *
 * Elle ne sait rien de ce qu'elle contient (étape, champs, erreurs) et ne décide pas du thème :
 * on ne connaît pas encore la persona de celui qui se connecte.
 */
import React from "react"
import { useWindowDimensions, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { IconButton } from "../../atoms/Buttons/IconButton"
import { Logo } from "../../atoms/Logo"
import { Card } from "../../molecules/Card"
import { breakpoints } from "../../theme/tokens/breakpoints"
import { PageTemplate } from "../PageTemplate"
import { ScreenBackground } from "../ScreenBackground"

/** La colonne d'un formulaire de connexion — 400 px, la largeur du legacy. */
export const AUTH_CONTENT_WIDTH = 400
/** La colonne d'une grille (le choix du compte) — les 600 px du legacy. */
const AUTH_WIDE_WIDTH = 600
/** La hauteur du logo de tête — une tête sur mesure s'y aligne. */
export const AUTH_LOGO_HEIGHT = 32

const WIDE_FROM = breakpoints.md
/**
 * Les marges de la page (20) et, pour un formulaire en carte, de la carte (32), de chaque côté de la
 * colonne. L'accueil n'a pas de carte : sa colonne garde ses 400 px sans les marges de la carte.
 */
const PAGE_WIDTH = {
    form: AUTH_CONTENT_WIDTH + 2 * (20 + 32),
    welcome: AUTH_CONTENT_WIDTH + 2 * 20,
    wide: AUTH_WIDE_WIDTH + 2 * 20,
}
const HEADER_HEIGHT = 56
/** Le logo de l'accueil, dans la page : le livre le pose en grand, en haut de l'écran de connexion. */
const WELCOME_LOGO_HEIGHT = 64
const BACK_BUTTON_SIZE = 44

export type AuthTemplateProps = {
    children: React.ReactNode
    /** Le geste de sortie, en tête à gauche. Absent → ni flèche ni croix. */
    onBack?: () => void
    /** `back` (la flèche, défaut) ou `close` (la croix, pour un écran ouvert depuis un lien). */
    leading?: "back" | "close"
    /** Ce que la tête montre à la place du logo seul — la question « Qui utilise Masteur ? ». */
    header?: React.ReactNode
    /**
     * `form` (défaut) : la colonne de 400 px, dans une carte à partir de `md`. `wide` : 600 px,
     * sans carte — une grille de tuiles n'est pas un formulaire.
     */
    column?: "form" | "wide"
    /** Centre la colonne dans la hauteur — un écran d'attente ou d'annonce, sans formulaire. */
    centered?: boolean
    /**
     * L'accueil de la connexion, tel que le livre le dessine : la lueur, le logo en grand en haut de
     * la page (plus dans la tête), et pas de carte — la colonne prend la hauteur de l'écran, pour que
     * l'étape puisse tenir son action en bas sur un téléphone. Défaut `false`.
     */
    welcome?: boolean
    testID?: string
}

export function AuthTemplate(props: AuthTemplateProps) {
    const {
        children,
        onBack,
        leading = "back",
        header,
        column = "form",
        centered = false,
        welcome = false,
        testID,
    } = props
    const { width } = useWindowDimensions()
    const carded = column === "form" && !welcome && width >= WIDE_FROM

    const head = (
        <View style={styles.header}>
            {welcome ? null : (header ?? <Logo height={AUTH_LOGO_HEIGHT} />)}
            {onBack != null && (
                <View style={styles.back}>
                    <IconButton
                        iconName={leading === "close" ? "times" : "chevron-left"}
                        appearance="transparent"
                        iconColor="primary"
                        size={BACK_BUTTON_SIZE}
                        onPress={onBack}
                        accessibilityLabel={leading === "close" ? "Fermer" : "Retour"}
                    />
                </View>
            )}
        </View>
    )

    return (
        // La lueur va à l'accueil seul : les étapes suivantes sont des formulaires, où le livre l'interdit.
        <ScreenBackground glow={welcome} testID={testID}>
            <PageTemplate
                header={head}
                maxContentWidth={PAGE_WIDTH[welcome ? "welcome" : column]}
                padding="5"
                keyboardAware
            >
                <View style={styles.body(centered)}>
                    {/* Toujours le même élément : franchir `md` (une tablette qu'on tourne) change un
                        style, il ne remonte pas le formulaire — le champ garde son focus et le clavier. */}
                    <Card
                        p={carded ? "8" : "0"}
                        elevation={carded ? "card" : "none"}
                        style={[carded ? undefined : styles.bare, welcome ? styles.welcome : undefined]}
                    >
                        {welcome && (
                            <View style={styles.welcomeLogo}>
                                <Logo height={WELCOME_LOGO_HEIGHT} />
                            </View>
                        )}
                        {children}
                    </Card>
                </View>
            </PageTemplate>
        </ScreenBackground>
    )
}

const styles = StyleSheet.create((theme) => ({
    header: {
        height: HEADER_HEIGHT,
        alignItems: "center",
        justifyContent: "center",
    },
    back: {
        position: "absolute",
        left: theme.spacing["1"],
        top: (HEADER_HEIGHT - BACK_BUTTON_SIZE) / 2,
    },
    body: (centered: boolean) => ({
        flex: 1,
        justifyContent: centered ? ("center" as const) : ("flex-start" as const),
        paddingTop: theme.spacing["2"],
        paddingBottom: theme.spacing["6"],
    }),
    /** Sous `md`, ou pour une grille : la colonne est posée à même la page. */
    bare: {
        backgroundColor: "transparent",
    },
    welcome: {
        flex: 1,
    },
    welcomeLogo: {
        alignItems: "center",
        paddingTop: { xs: theme.spacing["6"], md: theme.spacing["10"] },
        paddingBottom: theme.spacing["8"],
    },
}))
